import type { LegalPolicy } from './legal.content';

/**
 * Customer-facing policies (Part A of the business's legal pack, supplied 2026-09-21).
 * Shown on /customer/profile and on the public /legal page's "For customers" tab.
 * The wording is the business's own — square-bracketed values are theirs to finalise.
 */
export const CUSTOMER_POLICIES: readonly LegalPolicy[] = [
  {
    key: 'terms',
    title: 'Terms and Conditions',
    sub: 'The agreement between you and Trexa',
    blocks: [
      { k: 'h', t: '1. About these Terms' },
      { k: 'p', t: 'These Terms govern your use of the Trexa website and app ("Trexa", "we", "us"), operated by Inspiration Valley Enterprise (SSM: 002746500-T). By creating an account, making a booking or paying through Trexa, you agree to these Terms, together with our Privacy Policy and Refund Policy.' },

      { k: 'h', t: '2. What Trexa does' },
      { k: 'p', t: 'Trexa is a booking and payment-protection platform for wedding and event services. Venues and vendors ("Vendors") list their packages on Trexa. When you book, your payments are held in protected custody and released to the Vendor only when the conditions in these Terms are met.' },
      { k: 'p', t: "Trexa is not the provider of the wedding services. Your service contract is with the Vendor. Trexa's role is to manage the booking record, hold and release payments according to the rules below, and help resolve disputes." },

      { k: 'h', t: '3. Your account' },
      { k: 'p', t: 'You must be at least 18 years old and give accurate information. You are responsible for all activity under your account and for keeping your login details secure. Tell us immediately at support@ivalley.my if you suspect unauthorised access.' },

      { k: 'h', t: '4. Bookings' },
      { k: 'li', m: 'a)', t: 'A booking may include services from one venue and one or more partner Vendors, shown to you as a single order and invoice.' },
      { k: 'li', m: 'b)', t: 'A booking is confirmed only once the Vendor has accepted it and your deposit has been received into protected custody.' },
      { k: 'li', m: 'c)', t: 'The invoice shows the package details, event date, total price, deposit, balance and due dates. Please check it carefully before paying.' },
      { k: 'li', m: 'd)', t: 'If the Vendor revises the order (e.g. change in pax or items), you will receive a revised invoice. Changes take effect only after you accept them.' },

      { k: 'h', t: '5. Payments and protected custody' },
      { k: 'li', m: 'a)', t: 'Payments are processed by Razorpay Curlec, a payment provider regulated in Malaysia. Trexa does not store your full card or bank login details.' },
      { k: 'li', m: 'b)', t: "Your deposit and balance are held through Razorpay Curlec's marketplace settlement facility, separate from the Vendor's own accounts, and are not paid to the Vendor at the time you pay." },
      { k: 'li', m: 'c)', t: 'Funds are released to the Vendor according to the milestone schedule shown on your invoice, and in full after the event is completed (see Clause 7).' },
      { k: 'li', m: 'd)', t: 'Customer Protection Fee. A protection fee is added on top of your order total and is shown clearly before you pay. It covers payment protection, custody and dispute handling. Its refundability is set out in the Refund Policy.' },
      { k: 'li', m: 'e)', t: 'Prices are in Malaysian Ringgit (RM) and include any applicable taxes unless stated otherwise.' },

      { k: 'h', t: '6. Balance payment' },
      { k: 'p', t: 'The full balance must be paid into custody by [1 day] before the event date. If the balance is not received by then, the booking is treated as cancelled by you and your deposit is forfeited under the Refund Policy.' },

      { k: 'h', t: '7. Completion and release of funds' },
      { k: 'li', m: 'a)', t: 'After your event, you will be asked to confirm that the services were delivered.' },
      { k: 'li', m: 'b)', t: 'Once you confirm, or if you do not confirm or raise a dispute within [X] days after the event date, the remaining funds are released to the Vendor.' },
      { k: 'li', m: 'c)', t: 'If you raise a dispute within that window, the disputed amount stays in custody until the dispute is resolved.' },

      { k: 'h', t: '8. Changes to Vendors (partner substitution)' },
      { k: 'p', t: 'If a partner Vendor in your booking can no longer perform, the venue may offer a substitute Vendor at the price you were originally quoted. You can choose to accept the substitute or to receive a refund for that portion of the booking. We will not apply a substitute without your consent.' },

      { k: 'h', t: '9. Cancellations and refunds' },
      { k: 'p', t: 'Cancellations and refunds are governed by our Refund Policy, which forms part of these Terms.' },

      { k: 'h', t: '10. Disputes' },
      { k: 'li', m: 'a)', t: 'Raise a dispute through the booking page or at support@ivalley.my within [X] days after the event date, with supporting details (photos, messages, contract terms).' },
      { k: 'li', m: 'b)', t: 'We will review information from both sides and aim to reach a decision within [14] working days. We may release, partially release, or refund the funds held based on the evidence.' },
      { k: 'li', m: 'c)', t: 'Our decision on the funds held in custody does not stop you from pursuing your legal rights against the Vendor, including through the Tribunal for Consumer Claims Malaysia.' },

      { k: 'h', t: '11. Your responsibilities' },
      { k: 'p', t: 'You agree not to: give false information, use the platform for fraud or money laundering, arrange to pay the Vendor outside Trexa for a booking made on Trexa (which removes your protection), or misuse, copy or interfere with the platform.' },

      { k: 'h', t: '12. Limitation of liability' },
      { k: 'li', m: 'a)', t: 'Vendors are solely responsible for the quality, safety and delivery of their services.' },
      { k: 'li', m: 'b)', t: "To the extent permitted by law, Trexa's total liability to you for any claim relating to a booking is limited to the amount you paid through Trexa for that booking." },
      { k: 'li', m: 'c)', t: 'Nothing in these Terms excludes rights you have under the Consumer Protection Act 1999 that cannot be excluded by law.' },

      { k: 'h', t: '13. Suspension' },
      { k: 'p', t: 'We may suspend or close accounts involved in fraud, abuse or breach of these Terms. Funds held for active bookings will still be handled according to these Terms.' },

      { k: 'h', t: '14. Changes to these Terms' },
      { k: 'p', t: 'We may update these Terms from time to time. Changes do not affect bookings that were already confirmed before the change. We will notify you of material changes by email or in-app.' },

      { k: 'h', t: '15. Governing law' },
      { k: 'p', t: 'These Terms are governed by the laws of Malaysia. Disputes are subject to the jurisdiction of the courts of Kuala Lumpur, without affecting your right to use the Tribunal for Consumer Claims.' },
    ],
  },

  {
    key: 'privacy',
    title: 'Privacy Policy',
    sub: 'What we collect and how we use it',
    blocks: [
      { k: 'p', t: 'Inspiration Valley Enterprise ("Trexa") respects your privacy and handles your personal data in line with the Personal Data Protection Act 2010 (PDPA). This notice is available in English and Bahasa Malaysia.' },

      { k: 'h', t: '1. Data we collect' },
      { k: 'li', t: 'Identity & contact: name, phone number, email, IC or passport number (only where required for payment or dispute verification).' },
      { k: 'li', t: 'Booking details: event date, venue, guest count, package selections, invoices, messages with Vendors.' },
      { k: 'li', t: 'Payment data: transaction references, amounts, payment status. Card and bank details are processed by Razorpay Curlec; we do not store full card numbers.' },
      { k: 'li', t: 'Technical data: device, browser, IP address, log and usage data, cookies.' },

      { k: 'h', t: '2. Why we use it' },
      { k: 'li', t: 'To create your account and process bookings.' },
      { k: 'li', t: 'To collect, hold, release and refund payments.' },
      { k: 'li', t: 'To share necessary booking details with the Vendors you book.' },
      { k: 'li', t: 'To send booking updates, reminders and payment due notices.' },
      { k: 'li', t: 'To handle disputes, prevent fraud and meet legal obligations (including anti-money-laundering requirements).' },
      { k: 'li', t: 'To improve our services. Marketing messages are sent only with your consent, and you can opt out anytime.' },

      { k: 'h', t: '3. Who we share it with' },
      { k: 'li', t: 'Vendors in your booking — only what they need to deliver the service.' },
      { k: 'li', t: 'Razorpay Curlec — to process, hold and settle payments.' },
      { k: 'li', t: 'Service providers — hosting, email/SMS/WhatsApp messaging, analytics, under confidentiality obligations.' },
      { k: 'li', t: 'Authorities — where required by law or court order.' },
      { k: 'p', t: 'We do not sell your personal data.' },

      { k: 'h', t: '4. Cross-border transfer' },
      { k: 'p', t: 'Some service providers (e.g. cloud hosting) may store data outside Malaysia. We only transfer data where the destination has adequate protection or appropriate safeguards are in place, as permitted under the PDPA.' },

      { k: 'h', t: '5. How long we keep it' },
      { k: 'p', t: 'We keep booking and payment records for [7] years to meet accounting and legal requirements. Other data is deleted or anonymised when no longer needed.' },

      { k: 'h', t: '6. Security' },
      { k: 'p', t: 'We use encryption in transit, access controls and regular reviews to protect your data. If a data breach occurs that is likely to cause significant harm, we will notify the Personal Data Protection Commissioner and affected users as required by law.' },

      { k: 'h', t: '7. Your rights' },
      { k: 'p', t: 'You may request to access, correct, or port your personal data, withdraw consent for marketing, or ask us to limit processing. Contact our Data Protection Officer at support@ivalley.my. We will respond within 21 days. We may charge a fee permitted by law for access requests.' },

      { k: 'h', t: '8. Cookies' },
      { k: 'p', t: 'We use essential cookies to run the platform and optional analytics cookies to improve it. You can control cookies in your browser settings.' },

      { k: 'h', t: '9. Changes' },
      { k: 'p', t: 'We may update this policy and will post the latest version on this page.' },

      { k: 'h', t: '10. Contact' },
      { k: 'p', t: 'Data Protection Officer, Inspiration Valley Enterprise, Plaza Damas 1, Taman Sri Hartamas, 50480 Kuala Lumpur — support@ivalley.my' },
    ],
  },

  {
    key: 'refund',
    title: 'Refund Policy',
    sub: 'Cancellations, refunds and how money moves',
    blocks: [
      { k: 'p', t: 'Your money is held in protected custody until the Vendor delivers. This policy explains what happens if a booking is cancelled or changed.' },

      { k: 'h', t: '1. Before your deposit is paid' },
      { k: 'p', t: 'You can withdraw a booking request at any time before paying the deposit. Nothing is charged.' },

      { k: 'h', t: '2. If you cancel' },
      {
        k: 'tier',
        t: 'More than [3] days before the event',
        rows: [['Deposit', 'Full refund'], ['Balance paid', 'Full refund'], ['Protection fee', '[Refunded / Non-refundable]']],
      },
      {
        k: 'tier',
        t: 'Within [3] days of the event',
        rows: [['Deposit', 'Forfeited'], ['Balance paid', 'Refunded'], ['Protection fee', 'Non-refundable']],
      },
      {
        k: 'tier',
        t: 'Balance not paid by [1] day before the event',
        rows: [['Deposit', 'Forfeited (booking auto-cancelled)'], ['Protection fee', 'Non-refundable']],
      },
      { k: 'p', t: 'A forfeited deposit is paid to the Vendor as compensation for holding your date, or to the Trexa guarantee fund, according to platform policy at the time of booking.' },

      { k: 'h', t: '3. If the Vendor cancels' },
      { k: 'p', t: 'You get a full refund of everything you paid, including the Customer Protection Fee.' },

      { k: 'h', t: '4. If a partner Vendor drops out' },
      { k: 'p', t: 'You can accept a substitute Vendor at the original quoted price, or get a refund for that portion of the booking. The rest of your booking continues unaffected.' },

      { k: 'h', t: '5. If the service is not delivered as agreed' },
      { k: 'p', t: 'Raise a dispute within [X] days after the event (see Terms, Clause 10). Funds remain in custody until the dispute is decided, and a full or partial refund may be issued.' },

      { k: 'h', t: '6. Revised orders' },
      { k: 'p', t: 'If a revised invoice lowers the total and you have already paid more than the new amount, the difference is refunded or credited as agreed with you.' },

      { k: 'h', t: '7. How refunds are paid' },
      { k: 'li', t: 'Refunds are made to your original payment method, or to a Malaysian bank account in your name.' },
      { k: 'li', t: 'Approved refunds are processed within [7] working days. Banks may take a further [3–14] working days to show the amount.' },
      { k: 'li', t: 'Any bank or payment charges not caused by you are borne by Trexa or the Vendor.' },

      { k: 'h', t: '8. Contact' },
      { k: 'p', t: 'Refund questions: support@ivalley.my | +60 18-703 5797' },
    ],
  },

  {
    key: 'about',
    title: 'About Us',
    sub: 'Your wedding deposit, protected',
    blocks: [
      { k: 'p', t: "Paying a big wedding deposit shouldn't feel like a gamble. Too many couples have lost money to halls and vendors that closed down, cancelled last minute, or simply disappeared." },
      { k: 'p', t: "Trexa fixes that. When you book a venue or vendor through Trexa, your payment doesn't go straight into their pocket. It's held in protected custody and released only when agreed milestones are met, and in full only after your event is done." },

      { k: 'h', t: 'How it works' },
      { k: 'li', m: '1.', t: 'Book — choose your hall and packages. One invoice, even if several vendors are involved.' },
      { k: 'li', m: '2.', t: 'Pay into custody — your deposit and balance are held safely, not spent by the vendor in advance.' },
      { k: 'li', m: '3.', t: 'Celebrate — enjoy your day.' },
      { k: 'li', m: '4.', t: "Confirm — the vendor gets paid once the job is done. If something goes wrong, you're protected." },

      { k: 'h', t: 'Why Trexa' },
      { k: 'li', t: 'Payments processed through Razorpay Curlec, a regulated Malaysian payment provider' },
      { k: 'li', t: 'Funds held separately from vendor accounts through Razorpay Curlec' },
      { k: 'li', t: 'Clear refund rules, shown before you pay' },
      { k: 'li', t: "Real people to help if there's a dispute" },

      { k: 'p', t: 'Built in Kuala Lumpur by Inspiration Valley Enterprise, starting with wedding halls across the Klang Valley.' },
    ],
  },

  {
    key: 'contact',
    title: 'Contact Us',
    sub: "We're here to help with your booking",
    blocks: [
      { k: 'li', t: 'Email: support@ivalley.my' },
      { k: 'li', t: 'WhatsApp: +60 18-703 5797' },
      { k: 'li', t: 'Phone: +60 18-703 5797' },
      { k: 'li', t: 'Hours: Monday–Friday, 9:00am–6:00pm (excluding public holidays). [Weekend support for events happening within 48 hours.]' },

      { k: 'p', t: 'For a faster response, include your booking ID and event date.' },
      { k: 'p', t: 'Disputes or payment issues: use the "Raise an issue" button on your booking page so we can hold the funds while we review.' },

      { k: 'h', t: 'Business address' },
      { k: 'p', t: 'Inspiration Valley Enterprise (SSM: 002746500-T)', tight: true },
      { k: 'p', t: 'Plaza Damas 1, Taman Sri Hartamas, 50480 Kuala Lumpur' },
    ],
  },
];
